/**
 * OfficeReportRanking — totais por escritório (6 indicadores), tabela de
 * conversão por etapa e detalhe mensal expansível por escritório.
 */
import { Fragment, useState } from "react";
import { ChevronDown, ChevronRight, TrendingUp, Percent } from "lucide-react";
import { METRICS, RATES, pct } from "@/lib/officeReport";
import { monthLabel, spansYears } from "./OfficeReportMatrix";

const fmt = (n) => (n || 0).toLocaleString("pt-PT");
const TH = "text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold bg-muted/30 border-b border-border";

function OfficeDetail({ office, withYear }) {
  return (
    <tr data-testid={`office-report-detail-${office.office}`}>
      <td colSpan={METRICS.length + 2} className="p-0 bg-muted/20">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
              <th className="text-left py-1.5 px-4">Mês</th>
              {METRICS.map((m) => <th key={m.key} className="text-right py-1.5 px-3">{m.short}</th>)}
              <th className="text-right py-1.5 px-3">Lead→Ganha</th>
            </tr>
          </thead>
          <tbody>
            {office.months.map((b) => (
              <tr key={b.key} className="border-t border-border/40">
                <td className="py-1.5 px-4 text-foreground whitespace-nowrap">{monthLabel(b.key, withYear)}</td>
                {METRICS.map((m) => (
                  <td key={m.key} className="py-1.5 px-3 text-right tabular-nums" style={{ color: b[m.key] ? m.color : undefined }}>
                    {b[m.key] ? fmt(b[m.key]) : <span className="text-muted-foreground/40">·</span>}
                  </td>
                ))}
                <td className="py-1.5 px-3 text-right tabular-nums text-muted-foreground">
                  {b.leads ? pct((b.ganhos / b.leads) * 100) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    </tr>
  );
}

function RankingTable({ offices, open, toggle, withYear }) {
  return (
    <div className="rounded-lg border border-border/60 overflow-x-auto">
      <table className="w-full text-sm" data-testid="office-report-ranking-table">
        <thead>
          <tr className={TH}>
            <th className="text-left py-2.5 px-3">Escritório</th>
            {METRICS.map((m) => <th key={m.key} className="text-right py-2.5 px-3">{m.short}</th>)}
            <th className="text-right py-2.5 px-3" title="Ganhas ÷ Leads">Lead→Ganha</th>
          </tr>
        </thead>
        <tbody>
          {offices.map((o) => (
            <Fragment key={o.office}>
              <tr
                onClick={() => toggle(o.office)}
                className="border-b border-border/60 hover:bg-muted/30 cursor-pointer"
                data-testid={`office-report-ranking-row-${o.office}`}
              >
                <td className="py-2 px-3 font-medium text-foreground whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5">
                    {open[o.office] ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                    {o.office}
                  </span>
                </td>
                {METRICS.map((m) => (
                  <td key={m.key} className="py-2 px-3 text-right tabular-nums font-semibold" style={{ color: m.color }}>{fmt(o.totals[m.key])}</td>
                ))}
                <td className="py-2 px-3 text-right tabular-nums font-semibold text-[#2E7D4F]">{pct(o.rates.lead_to_ganho_pct)}</td>
              </tr>
              {open[o.office] && <OfficeDetail office={o} withYear={withYear} />}
            </Fragment>
          ))}
          {offices.length === 0 && (
            <tr><td colSpan={METRICS.length + 2} className="py-6 text-center text-sm text-muted-foreground">Sem actividade no período.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ConversionTable({ offices }) {
  return (
    <div className="rounded-lg border border-border/60 overflow-x-auto">
      <table className="w-full text-sm" data-testid="office-report-conversion-table">
        <thead>
          <tr className={TH}>
            <th className="text-left py-2.5 px-3">Escritório</th>
            {RATES.map((r) => <th key={r.key} className="text-right py-2.5 px-3" title={r.title}>{r.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {offices.map((o) => (
            <tr key={o.office} className="border-b border-border/60 last:border-b-0" data-testid={`office-report-conversion-row-${o.office}`}>
              <td className="py-2 px-3 font-medium text-foreground whitespace-nowrap">{o.office}</td>
              {RATES.map((r, i) => (
                <td key={r.key} className={`py-2 px-3 text-right tabular-nums ${i === RATES.length - 1 ? "font-semibold text-[#2E7D4F]" : "text-muted-foreground"}`}>
                  {pct(o.rates[r.key])}
                </td>
              ))}
            </tr>
          ))}
          {offices.length === 0 && (
            <tr><td colSpan={RATES.length + 1} className="py-6 text-center text-sm text-muted-foreground">Sem actividade no período.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function OfficeReportRanking({ offices, months }) {
  const [open, setOpen] = useState({});
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));
  const withYear = spansYears(months);
  return (
    <>
      <div className="bg-card border border-border rounded-2xl p-6" data-testid="office-report-ranking">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" /> Ranking por escritório
          </div>
          <div className="text-[10px] text-muted-foreground/70">Clique numa linha para ver o detalhe mensal</div>
        </div>
        <RankingTable offices={offices} open={open} toggle={toggle} withYear={withYear} />
      </div>
      <div className="bg-card border border-border rounded-2xl p-6" data-testid="office-report-conversion">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold flex items-center gap-1.5 mb-3">
          <Percent className="w-3 h-3" /> Conversão por etapa do funil
        </div>
        <ConversionTable offices={offices} />
      </div>
    </>
  );
}
